/**
 * The run-log writes (fieldiq-api#902): Log Run, Edit, Close Run, Delete Run.
 * Every build role — admin, office_staff, dispatcher, field_worker — may use
 * them; the screens check `canBuildDownhole` before rendering a button.
 *
 * THREE KINDS OF RECORD, the API's (`run_state`):
 *   open     run in, not yet out. Only while the assembly is in_hole, and only
 *            one at a time. Closed later by sending run_out_at.
 *   closed   both instants. Closing adds C&D hours (out − in), one run and the
 *            footage to every tool in the string — the server does that sum.
 *   not_run  mobilized to the well and never run in. No times, depths, hours,
 *            bit condition or LIH; still billable at the minimum.
 *
 * TIMES ARE THE TENANT'S WALL CLOCK. A `datetime-local` value has no offset,
 * and the API reads an offset-free value in the company's zone
 * (instantFromDateInput). So an Edmonton office logging a Chicago crew's run
 * types the time on the crew's clock, and the picker's initial value is built
 * from the instant in the tenant's zone, never the device's.
 *
 * MEASURES are typed in the tenant's units and sent imperial; footage, C&D
 * hours and the run number are the server's and are never sent (a 400).
 *
 * AN EDIT SENDS ONLY WHAT CHANGED — the component form's rule, for the same
 * reason (a metric depth opened and saved untouched is not a round trip), and
 * because an invoiced run refuses every key but `notes`.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteRun, useLogRun, useUpdateRun } from '../../hooks/useDownhole';
import { unwrapArray } from '../../lib/api-envelope';
import { parseInstantDate, zonedParts } from '../../lib/calendar-date';
import { inputUnit, parseInputValue, type DhComponentInputField } from '../../lib/downhole-component-input';
import { DH_BILLING_TYPE_LABEL } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { formatDh, toInputValue } from '../../lib/downhole-units';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import {
  DH_RUN_BILLING_TYPES,
  type DhAssemblyDetail,
  type DhRun,
  type DhRunState,
  type DhRunWrite,
  type DhUnitSystem,
} from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from './DownholeBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const DANGER =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/** The measured and counted run fields, in the component form's field shape. */
const MEASURES: DhComponentInputField[] = [
  { key: 'depth_in_ft', label: 'Depth in', kind: 'number', quantity: 'length' },
  { key: 'depth_out_ft', label: 'Depth out', kind: 'number', quantity: 'length' },
  { key: 'circulating_hours', label: 'Circulating', kind: 'number', unit: 'h' },
  { key: 'rotating_hours', label: 'Rotating', kind: 'number', unit: 'h' },
  { key: 'on_bottom_hours', label: 'On bottom', kind: 'number', unit: 'h' },
  { key: 'rop_avg', label: 'ROP avg', kind: 'number', quantity: 'rop' },
  { key: 'wob_avg_klbs', label: 'WOB avg', kind: 'number', quantity: 'force' },
  { key: 'rpm_avg', label: 'RPM avg', kind: 'number', unit: 'rpm' },
  { key: 'torque_avg_ftlbs', label: 'Torque avg', kind: 'number', quantity: 'torque' },
  { key: 'mud_weight', label: 'Mud weight', kind: 'number', quantity: 'density' },
  { key: 'flow_rate_gpm', label: 'Flow rate', kind: 'number', quantity: 'flow' },
];
const TEXTS: { key: string; label: string; max: number }[] = [
  { key: 'well_name', label: 'Well', max: 150 },
  { key: 'rig_name', label: 'Rig', max: 100 },
  { key: 'bit_condition', label: 'Bit condition', max: 50 },
  { key: 'failure_mode', label: 'Failure mode', max: 100 },
];
/** What the API freezes once the assembly is broken down. */
const COUNTER_KEYS = new Set(['run_in_at', 'run_out_at', 'depth_in_ft', 'depth_out_ft']);

/** An instant as the tenant's wall clock, for a datetime-local input. '' when absent or zone unknown. */
export function tenantLocalInput(instant: unknown, tz: string | null): string {
  const at = instant instanceof Date ? instant : parseInstantDate(instant);
  const p = at ? zonedParts(at, tz) : null;
  if (!p) return '';
  const two = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${two(p.month)}-${two(p.day)}T${two(p.hour)}:${two(p.minute)}`;
}

type Values = Record<string, string>;

const storedFeet = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Depth out against depth in, as the API checks it: on the merged row, a
 * depth_out_ft below depth_in_ft is a 400 (downhole.routes.ts checkRunShape).
 *
 * BOTH SIDES ARE STORED FEET — what the API will hold after this save — never
 * the metres on screen, so a metric rounding cannot block a save the API would
 * take. Equal is allowed (no footage), and either side absent is no check:
 * depth out is optional. The depth in is printed in the tenant's units, to the
 * precision its own input shows, so "14,980 ft" and "4,565.9 m" read as typed.
 */
export function depthOutProblem(depthInFt: number | null, depthOutFt: number | null, units: DhUnitSystem | null): string | null {
  if (depthInFt === null || depthOutFt === null || depthOutFt >= depthInFt) return null;
  return `Depth out can't be shallower than depth in (${formatDh(depthInFt, 'length', units, { digits: 2, minDigits: 0 })}).`;
}

/**
 * The typed depth out in stored feet — except that a depth out typed EXACTLY as
 * this form shows the stored depth in is that depth in, to the hundredth of a
 * foot. A metric reader sees 14,980 ft as `4565.9`; typed back, 4565.9 m is
 * 14,979.99 ft, which the API refuses as shallower. Equal means equal.
 */
function depthOutFeet(raw: string, parsed: number, depthInFt: number | null, units: DhUnitSystem | null): number {
  if (depthInFt !== null && raw.trim() !== '' && Number(raw.trim()) === Number(toInputValue(depthInFt, 'length', units))) return depthInFt;
  return parsed;
}

/** The plain line under Depth out, when it is shallower than depth in. */
function DepthOutError({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-1 border-l-2 border-fi-signal-red bg-fi-signal-red/10 px-2 py-1 text-xs text-fieldiq-text">
      {message}
    </p>
  );
}

function valuesFrom(run: DhRun | null, units: DhUnitSystem | null, tz: string | null, techId: string): Values {
  const v: Values = {
    run_in_at: run ? tenantLocalInput(run.run_in_at, tz) : '',
    run_out_at: run ? tenantLocalInput(run.run_out_at, tz) : '',
    billing_type: run?.billing_type ?? 'per_cd_run',
    field_tech_id: run ? run.field_tech_id ?? '' : techId,
    notes: run?.notes ?? '',
    lih: run?.lih_flag ? 'yes' : '',
    lih_component_id: run?.lih_component_id ?? '',
  };
  for (const m of MEASURES) {
    const stored = run ? (run as unknown as Record<string, unknown>)[m.key] : null;
    v[m.key] = m.quantity ? toInputValue(stored, m.quantity, units) : stored == null ? '' : String(stored);
  }
  for (const t of TEXTS) v[t.key] = run ? ((run as unknown as Record<string, string | null>)[t.key] ?? '') : '';
  return v;
}

/** Every tool that was in the string, current or removed — LIH picks from these. */
function membersOf(asm: DhAssemblyDetail) {
  const seen = new Map<string, { id: string; serial: string }>();
  for (const m of [...asm.components, ...asm.removed_components]) {
    if (!seen.has(m.component_id)) seen.set(m.component_id, { id: m.component_id, serial: m.serial_number });
  }
  return Array.from(seen.values());
}

function useTechs() {
  return useQuery({
    queryKey: ['users', 'downhole-tech-picker'],
    queryFn: async () =>
      unwrapArray<{ id: string; firstName?: string | null; lastName?: string | null; email?: string | null; role?: string }>(
        (await api.users.list()).data,
      ),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

const techName = (u: { firstName?: string | null; lastName?: string | null; email?: string | null }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || '—';

/**
 * Log Run (with `assembly` and no `run`) or Edit (with both).
 *
 * `invoiced` is known from the invoice list (a run read does not say), and an
 * invoiced run takes notes only — every other input is locked and says why.
 */
export function RunFormDialog({
  open,
  onClose,
  assembly,
  run = null,
  invoiced = false,
  units,
  tz,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  assembly: DhAssemblyDetail;
  run?: DhRun | null;
  invoiced?: boolean;
  units: DhUnitSystem | null;
  tz: string | null;
  onSaved?: (run: DhRun | null) => void;
}) {
  const { user } = useAuth();
  const log = useLogRun();
  const update = useUpdateRun();
  const techs = useTechs();
  const editing = !!run;
  const busy = log.isPending || update.isPending;

  const canOpen = assembly.status === 'in_hole';
  const initialKind: DhRunState = run?.run_state ?? (run ? (run.run_in_at ? (run.run_out_at ? 'closed' : 'open') : 'not_run') : canOpen ? 'open' : 'closed');
  const [kind, setKind] = useState<DhRunState>(initialKind);
  // The baseline is taken when the dialog OPENS and held until it closes: what
  // the reader started from is what "changed" is measured against, and a runs
  // refetch while it is open (a new `run` object for the same row) neither
  // resets the inputs nor moves the baseline under them.
  const opened = useOpenCount(open);
  const [initial, setInitial] = useState<Values>(() => valuesFrom(run, units, tz, user?.id ?? ''));
  const [values, setValues] = useState<Values>(initial);
  const [depthError, setDepthError] = useState<string | null>(null);
  const depthOutRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (opened === 0) return;
    const start = valuesFrom(run, units, tz, user?.id ?? '');
    setInitial(start);
    setValues(start);
    setKind(initialKind);
    setDepthError(null);
    // Only on opening — see above. run / units / tz are read as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const frozen = assembly.status === 'disassembled';
  const locked = (key: string) => (invoiced && key !== 'notes') || (frozen && (COUNTER_KEYS.has(key) || key === 'lih'));
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (key === 'depth_in_ft' || key === 'depth_out_ft') setDepthError(null);
  };
  const members = membersOf(assembly);
  const notRun = kind === 'not_run';

  const build = (): DhRunWrite | null => {
    const body: DhRunWrite = {};
    const problems: string[] = [];
    let depthProblem: string | null = null;
    const changed = (key: string) => !editing || values[key] !== initial[key];
    const put = (key: string, value: string | number | boolean | null) => {
      if (value === null || value === '') {
        if (editing) body[key] = null;
      } else body[key] = value;
    };

    if (!notRun) {
      for (const key of ['run_in_at', 'run_out_at'] as const) {
        if (!changed(key)) continue;
        const raw = values[key].trim();
        if (raw && !LOCAL.test(raw)) problems.push(`${key === 'run_in_at' ? 'Run in' : 'Run out'} must be a date and time`);
        else put(key, raw || null);
      }
      if (!values.run_in_at.trim()) problems.push('Run in is required');
      if (kind === 'closed' && !values.run_out_at.trim()) problems.push('Run out is required for a completed run');
      if (editing && initial.run_out_at && !values.run_out_at.trim()) problems.push('A closed run cannot be reopened; correct its times instead');
      if (values.run_in_at && values.run_out_at && values.run_out_at < values.run_in_at) problems.push('Run out cannot be before run in');
      // A depth not changed is the stored one; the API checks the merged row.
      let depthIn = storedFeet(run?.depth_in_ft);
      let depthOut = storedFeet(run?.depth_out_ft);
      let depthsParsed = true;
      for (const m of MEASURES) {
        if (!changed(m.key)) continue;
        const out = parseInputValue(m, values[m.key], units);
        if ('error' in out) {
          problems.push(out.error);
          if (m.key === 'depth_in_ft' || m.key === 'depth_out_ft') depthsParsed = false;
          continue;
        }
        let value = out.value;
        if (m.key === 'depth_in_ft') depthIn = storedFeet(value);
        if (m.key === 'depth_out_ft') {
          // Only a STORED depth in can be matched as shown; two typed depths convert alike.
          if (value !== null) value = depthOutFeet(values[m.key], Number(value), changed('depth_in_ft') ? null : storedFeet(run?.depth_in_ft), units);
          depthOut = storedFeet(value);
        }
        put(m.key, value);
      }
      if (depthsParsed) depthProblem = depthOutProblem(depthIn, depthOut, units);
    }
    setDepthError(depthProblem);
    for (const t of TEXTS) {
      if (notRun && t.key === 'bit_condition') continue;
      if (changed(t.key)) put(t.key, values[t.key].trim() || null);
    }
    if (changed('notes')) put('notes', values.notes.trim() || null);
    if (changed('billing_type')) body.billing_type = values.billing_type;
    if (changed('field_tech_id')) put('field_tech_id', values.field_tech_id || null);

    if (!notRun && (changed('lih') || changed('lih_component_id'))) {
      if (values.lih) {
        if (!values.lih_component_id) problems.push('Choose the tool that was lost in hole');
        body.lih_flag = true;
        body.lih_component_id = values.lih_component_id || null;
      } else if (editing) {
        body.lih_flag = false;
        body.lih_component_id = null;
      }
    }
    if (!editing && notRun) body.not_run = true;

    // An invoiced run takes notes only; whatever else changed is not sent.
    if (invoiced) for (const k of Object.keys(body)) if (k !== 'notes') delete body[k];
    if (frozen) for (const k of Object.keys(body)) if (COUNTER_KEYS.has(k) || k === 'lih_flag' || k === 'lih_component_id') delete body[k];

    if (depthProblem) depthOutRef.current?.focus();
    if (problems.length > 0) toast.error(problems.join('. '));
    if (problems.length > 0 || depthProblem) return null;
    return body;
  };

  const submit = async () => {
    const body = build();
    if (!body) return;
    if (!onlineOrSay(toast.error)) return;
    if (editing && Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      const result = editing && run
        ? await update.mutateAsync({ id: run.id, body })
        : await log.mutateAsync({ assemblyId: assembly.id, body });
      toast.success(editing ? `Run ${run?.run_number ?? ''} saved` : `Run ${result.data?.run_number ?? ''} logged`);
      onClose();
      onSaved?.(result.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, editing ? 'The run was not saved.' : 'The run was not logged.'));
    }
  };

  const tzNote = tz ? `On the ${tz.replace(/_/g, ' ')} clock` : undefined;
  const text = (key: string, label: string, max: number) => (
    <DhField key={key} label={label} hint={!editing && (key === 'well_name' || key === 'rig_name') ? `Blank uses the assembly's: ${(assembly as unknown as Record<string, string | null>)[key] || '—'}` : undefined}>
      <input className={DH_INPUT} value={values[key]} onChange={set(key)} maxLength={max} disabled={locked(key)} />
    </DhField>
  );

  const techList = (techs.data ?? []).filter((u) => u.role !== 'customer');
  const techKnown = !values.field_tech_id || techList.some((u) => u.id === values.field_tech_id);

  return (
    <DhDialog
      open={open}
      wide
      title={editing ? `Edit run ${run?.run_number ?? ''} · ${assembly.assembly_number}` : `Log run · ${assembly.assembly_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={busy} onClick={submit}>
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {editing ? 'Save run' : 'Log run'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {invoiced && (
          <p role="note" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
            This run is on an invoice. Only its notes can change.
          </p>
        )}
        {frozen && !invoiced && (
          <p role="note" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
            The assembly has been torn down, so this run's times, depths and lost-in-hole flag are final.
          </p>
        )}

        {!editing && (
          <fieldset>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">What is being logged</legend>
            <div className="flex flex-col gap-2 text-sm text-fieldiq-text sm:flex-row sm:flex-wrap sm:gap-5">
              {canOpen && (
                <label className="inline-flex items-center gap-2">
                  <input type="radio" name="run-kind" checked={kind === 'open'} onChange={() => setKind('open')} />
                  Running now — in the hole, not out yet
                </label>
              )}
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="run-kind" checked={kind === 'closed'} onChange={() => setKind('closed')} />
                A completed run — in and out
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="run-kind" checked={kind === 'not_run'} onChange={() => setKind('not_run')} />
                Mobilized, not run
              </label>
            </div>
            {notRun && (
              <p className="mt-2 text-xs text-fieldiq-muted">
                The tools went to the well and never ran. No times, depths or hours are recorded, and no tool's counters move.
              </p>
            )}
          </fieldset>
        )}

        {!notRun && (
          <fieldset>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Times</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <DhField label="Run in *" hint={tzNote}>
                <input type="datetime-local" className={DH_DATE_INPUT} value={values.run_in_at} onChange={set('run_in_at')} disabled={locked('run_in_at')} />
              </DhField>
              {kind !== 'open' || editing ? (
                <DhField label={kind === 'open' ? 'Run out — fill in to close the run' : 'Run out *'} hint={tzNote}>
                  <input type="datetime-local" className={DH_DATE_INPUT} value={values.run_out_at} onChange={set('run_out_at')} disabled={locked('run_out_at')} />
                </DhField>
              ) : null}
            </div>
          </fieldset>
        )}

        <fieldset>
          <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Where and who</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {text('well_name', 'Well', 150)}
            {text('rig_name', 'Rig', 100)}
            <DhField label="Field tech">
              <select className={DH_INPUT} value={values.field_tech_id} onChange={set('field_tech_id')} disabled={locked('field_tech_id')}>
                <option value="">Not recorded</option>
                {!techKnown && <option value={values.field_tech_id}>{run?.field_tech_name || (values.field_tech_id === user?.id ? 'You' : '—')}</option>}
                {techList.map((u) => (
                  <option key={u.id} value={u.id}>
                    {techName(u)}
                    {u.id === user?.id ? ' (you)' : ''}
                  </option>
                ))}
              </select>
            </DhField>
            <DhField label="Billing">
              <select className={DH_INPUT} value={values.billing_type} onChange={set('billing_type')} disabled={locked('billing_type')}>
                {DH_RUN_BILLING_TYPES.map((b) => (
                  <option key={b} value={b}>
                    {DH_BILLING_TYPE_LABEL[b]}
                  </option>
                ))}
              </select>
            </DhField>
          </div>
        </fieldset>

        {!notRun && (
          <fieldset>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Drilling</legend>
            <p className="mb-2 text-xs text-fieldiq-muted">Footage and C&amp;D hours are worked out from these by the server.</p>
            <div className="grid gap-4 sm:grid-cols-3">
              {MEASURES.map((m) => {
                const unit = inputUnit(m, units);
                const isDepthOut = m.key === 'depth_out_ft';
                return (
                  <div key={m.key} className="min-w-0">
                    <DhField label={`${m.label}${unit ? ` (${unit})` : ''}`}>
                      <input
                        ref={isDepthOut ? depthOutRef : undefined}
                        className={DH_INPUT}
                        inputMode="decimal"
                        value={values[m.key]}
                        onChange={set(m.key)}
                        disabled={locked(m.key)}
                        aria-invalid={isDepthOut && depthError ? true : undefined}
                        aria-describedby={isDepthOut && depthError ? 'run-form-depth-out-error' : undefined}
                      />
                    </DhField>
                    {isDepthOut && <DepthOutError id="run-form-depth-out-error" message={depthError} />}
                  </div>
                );
              })}
              {text('bit_condition', 'Bit condition', 50)}
              {text('failure_mode', 'Failure mode', 100)}
            </div>
          </fieldset>
        )}
        {notRun && <div className="grid gap-4 sm:grid-cols-2">{text('failure_mode', 'Reason / failure mode', 100)}</div>}

        {!notRun && (
          <fieldset>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Lost in hole</legend>
            <label className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
              <input
                type="checkbox"
                checked={!!values.lih}
                disabled={locked('lih')}
                onChange={(e) => setValues((v) => ({ ...v, lih: e.target.checked ? 'yes' : '', lih_component_id: e.target.checked ? v.lih_component_id : '' }))}
              />
              A tool was lost in the hole on this run
            </label>
            {values.lih && (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <DhField label="Which tool *" hint="It is retired (serial kept) and skipped at teardown. Clearing the flag puts it back.">
                  <select className={DH_INPUT} value={values.lih_component_id} onChange={set('lih_component_id')} disabled={locked('lih')}>
                    <option value="">Choose…</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.serial}
                      </option>
                    ))}
                  </select>
                </DhField>
              </div>
            )}
          </fieldset>
        )}

        <DhField label="Notes">
          <textarea className={`${DH_INPUT} h-24 py-2`} value={values.notes} onChange={set('notes')} maxLength={10000} />
        </DhField>
      </div>
    </DhDialog>
  );
}

/** Close an open run: run out (now, on the tenant's clock, by default), depth out, the hours. */
export function CloseRunDialog({
  open,
  onClose,
  run,
  units,
  tz,
  assembly = null,
}: {
  open: boolean;
  onClose: () => void;
  run: DhRun;
  units: DhUnitSystem | null;
  tz: string | null;
  /** With the assembly, a tool lost on this run can be flagged as it closes. */
  assembly?: DhAssemblyDetail | null;
}) {
  const update = useUpdateRun();
  const [lostId, setLostId] = useState('');
  const fields = MEASURES.filter((m) => ['depth_out_ft', 'circulating_hours', 'rotating_hours', 'on_bottom_hours'].includes(m.key));
  const [out, setOut] = useState('');
  const [values, setValues] = useState<Values>({});
  const [depthError, setDepthError] = useState<string | null>(null);
  const depthOutRef = React.useRef<HTMLInputElement>(null);

  // Reset on OPENING only (useOpenCount): the runs list refetching while this
  // is open hands back a new `run` object and must not wipe what was typed.
  const opened = useOpenCount(open);
  const [initial, setInitial] = useState<Values>(() => valuesFrom(run, units, tz, ''));
  React.useEffect(() => {
    if (opened === 0) return;
    const start = valuesFrom(run, units, tz, '');
    setOut(tenantLocalInput(new Date(), tz));
    setInitial(start);
    setValues(start);
    setLostId('');
    setDepthError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const submit = async () => {
    const problems: string[] = [];
    if (!LOCAL.test(out)) problems.push('Run out must be a date and time');
    const runIn = tenantLocalInput(run.run_in_at, tz);
    if (runIn && out && out < runIn) problems.push('Run out cannot be before run in');
    const body: DhRunWrite = { run_out_at: out };
    if (lostId) {
      body.lih_flag = true;
      body.lih_component_id = lostId;
    }
    const depthIn = storedFeet(run.depth_in_ft);
    for (const f of fields) {
      if (values[f.key] === initial[f.key]) continue;
      const r = parseInputValue(f, values[f.key] ?? '', units);
      if ('error' in r) problems.push(r.error);
      else if (r.value !== null) {
        body[f.key] = f.key === 'depth_out_ft' ? depthOutFeet(values[f.key], Number(r.value), depthIn, units) : r.value;
      }
    }
    const depthProblem = depthOutProblem(depthIn, storedFeet(body.depth_out_ft ?? run.depth_out_ft), units);
    setDepthError(depthProblem);
    if (depthProblem) depthOutRef.current?.focus();
    if (problems.length) toast.error(problems.join('. '));
    if (problems.length || depthProblem) return;
    if (!onlineOrSay(toast.error)) return;
    try {
      await update.mutateAsync({ id: run.id, body });
      toast.success(`Run ${run.run_number ?? ''} closed`);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The run was not closed.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={`Close run ${run.run_number ?? ''} · ${run.assembly_number ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={update.isPending} onClick={submit}>
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Close run
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fieldiq-text">
          Closing adds this run's C&amp;D hours (run in to run out), one run and its footage to every tool in the string.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <DhField label="Run out *" hint={tz ? `On the ${tz.replace(/_/g, ' ')} clock` : undefined}>
            <input type="datetime-local" className={DH_DATE_INPUT} value={out} onChange={(e) => setOut(e.target.value)} />
          </DhField>
          {fields.map((f) => {
            const unit = inputUnit(f, units);
            const isDepthOut = f.key === 'depth_out_ft';
            return (
              <div key={f.key} className="min-w-0">
                <DhField label={`${f.label}${unit ? ` (${unit})` : ''}`}>
                  <input
                    ref={isDepthOut ? depthOutRef : undefined}
                    className={DH_INPUT}
                    inputMode="decimal"
                    value={values[f.key] ?? ''}
                    aria-invalid={isDepthOut && depthError ? true : undefined}
                    aria-describedby={isDepthOut && depthError ? 'close-run-depth-out-error' : undefined}
                    onChange={(e) => {
                      setValues((v) => ({ ...v, [f.key]: e.target.value }));
                      if (isDepthOut) setDepthError(null);
                    }}
                  />
                </DhField>
                {isDepthOut && <DepthOutError id="close-run-depth-out-error" message={depthError} />}
              </div>
            );
          })}
          {assembly && !run.lih_flag && (
            <DhField label="Lost in hole" hint="Only if a tool did not come back. It is retired, serial kept.">
              <select className={DH_INPUT} value={lostId} onChange={(e) => setLostId(e.target.value)}>
                <option value="">Nothing was lost</option>
                {membersOf(assembly).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.serial}
                  </option>
                ))}
              </select>
            </DhField>
          )}
        </div>
      </div>
    </DhDialog>
  );
}

/** Delete a run, after a confirm that says what it undoes. */
export function DeleteRunDialog({ open, onClose, run, onDeleted }: { open: boolean; onClose: () => void; run: DhRun; onDeleted?: () => void }) {
  const remove = useDeleteRun();
  const closed = run.run_state === 'closed' || (!!run.run_in_at && !!run.run_out_at);
  const submit = async () => {
    if (!onlineOrSay(toast.error)) return;
    try {
      await remove.mutateAsync(run.id);
      toast.success(`Run ${run.run_number ?? ''} deleted`);
      onClose();
      onDeleted?.();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The run was not deleted.'));
    }
  };
  return (
    <DhDialog
      open={open}
      title={`Delete run ${run.run_number ?? ''} · ${run.assembly_number ?? ''}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={DANGER} disabled={remove.isPending} onClick={submit}>
            {remove.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Delete run
          </button>
        </>
      }
    >
      <p className="text-sm text-fieldiq-text">
        {closed
          ? "The run's hours, its one run and its footage come back off every tool in the string."
          : 'Nothing has been added to the tools yet; the record is removed.'}
        {run.lih_flag ? ' The tool marked lost in hole is put back in the string.' : ''} This cannot be undone.
      </p>
    </DhDialog>
  );
}
